// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.
// Leaf label renderer for the SVG phylogenetic tree viewer.
//
// Implements getLabelledLeafNodes() and makeTextPositionAccessor() as pure
// functions of the built graph and props.
//
// M7: Density thinning is implemented. computeLeavesPerLabel derives the
// leavesPerLabel stride from the font size, tree geometry, and view scale.
// Labels use a hybrid size rule: font-size is fontSize / max(1, scale), so
// below scale 1 they render in world space (growing and shrinking with the
// tree like the node markers and metadata blocks) and at scale >= 1 they lock
// to a constant on-screen size, capped at the configured fontSize. Thinning
// tracks the same hinge — scale-independent while world-space (a stable subset
// as you zoom out), then revealing more labels as you zoom in past the cap,
// where the leaves keep spreading but the text size holds.

import { el } from "./dom.js";
import { Angles, TreeTypes } from "../core/constants.js";
import { nodeLayoutScale } from "./node-scale.js";

/**
 * Compute leavesPerLabel — how many leaves share one label slot — from the
 * font size, tree geometry, and view scale. Includes finiteness/degenerate
 * guards: naive arithmetic can yield NaN/0 (e.g. radius 0, asin out of
 * domain), which would silently produce `index % NaN` -> zero labels.
 * Always returns an integer >= 1.
 *
 * Labels use the hybrid size cap (fontSize / max(1, scale)), so the stride
 * scales by max(1, scale): below scale 1 the text is world-space and the
 * subset is scale-independent (zooming out keeps the same labels); at
 * scale >= 1 the text is capped on-screen while leaves keep spreading, so the
 * stride grows with scale and more labels appear as you zoom in. A
 * non-finite/<=0 scale (e.g. before the first layout) still returns stride 1.
 *
 * @param {object} graph - Built graph (graph.root.totalLeaves/totalSubtreeLength, graph.stepScale, graph.branchScale).
 * @param {object} props - Props (props.type, props.fontSize).
 * @param {number} scale - Current view scale (svg.clientWidth / viewBox.w); applied as max(1, scale).
 * @returns {number} Integer >= 1.
 */
export function computeLeavesPerLabel(graph, props, scale) {
  const type = props.type;
  const fontSize = props.fontSize ?? 16;
  const totalLeaves = graph.root.totalLeaves;

  if (!Number.isFinite(scale) || scale <= 0) return 1;
  if (!Number.isFinite(totalLeaves) || totalLeaves <= 0) return 1;
  // fontSize feeds both branches; a non-finite/<=0 value would make the rect
  // path divide by 0/Infinity and escape the power-of-two snap as Infinity,
  // silently dropping all but the first label (index % Infinity === 0).
  if (!Number.isFinite(fontSize) || fontSize <= 0) return 1;

  if (type === TreeTypes.Circular || type === TreeTypes.Radial) {
    const branchScale = graph.branchScale ?? 1;
    const radius = graph.root.totalSubtreeLength * branchScale * Math.max(1, scale);
    if (!Number.isFinite(radius) || radius <= 0) return 1;
    const chordLength = fontSize * 0.75;
    const ratio = chordLength / (2 * radius);
    // asin domain is [-1, 1]; ratio >= 1 means the label chord exceeds the
    // circle diameter (tiny tree / huge font) -> at most one label fits.
    if (ratio >= 1) return totalLeaves;
    const angle = 2 * Math.asin(ratio);
    if (!Number.isFinite(angle) || angle <= 0) return 1;
    const maxNumOfLabels = Math.floor(Angles.Degrees360 / angle);
    if (maxNumOfLabels <= 0) return totalLeaves;
    return Math.max(1, Math.ceil(totalLeaves / maxNumOfLabels));
  }

  const stepScale = graph.stepScale ?? 1;
  const treeLength = totalLeaves * stepScale * Math.max(1, scale);
  if (!Number.isFinite(treeLength) || treeLength <= 0) return 1;
  const maxNumOfLabels = treeLength / fontSize;
  const leavesPerLabel = totalLeaves / maxNumOfLabels;
  if (!(leavesPerLabel > 1)) return 1;
  // Snap up to the next power of two.
  return 2 ** Math.ceil(Math.log(leavesPerLabel) / Math.log(2));
}

/**
 * Return the leaf nodes that should carry a visible label, given a computed
 * leavesPerLabel stride. For Circular/Radial trees the last
 * ceil(leavesPerLabel/2) leaves are trimmed so thinned labels do not overlap
 * across the 0/2π seam.
 *
 * @param {object} graph - Built graph (graph.leaves).
 * @param {number} leavesPerLabel - Integer >= 1 stride.
 * @param {string} type - Tree type (TreeTypes value).
 * @returns {object[]}
 */
export function getLabelledLeafNodes(graph, leavesPerLabel, type) {
  if (leavesPerLabel <= 1) {
    const nodesWithLabels = [];
    for (const node of graph.leaves) {
      if (node.label) nodesWithLabels.push(node);
    }
    return nodesWithLabels;
  }

  const seamTrim =
    type === TreeTypes.Circular || type === TreeTypes.Radial
      ? Math.ceil(leavesPerLabel / 2)
      : 0;
  const lastIndex = graph.leaves.length - seamTrim;

  const nodesWithLabels = [];
  for (let index = 0; index < graph.leaves.length; index++) {
    if (index < lastIndex && index % leavesPerLabel === 0) {
      const node = graph.leaves[index];
      if (node.label) nodesWithLabels.push(node);
    }
  }
  return nodesWithLabels;
}

// ---------------------------------------------------------------------------
// Text position accessor
// ---------------------------------------------------------------------------
//
// Implementation notes:
//
//   Returns a closure `(node) => [x, y]` built from four captured values:
//   alignLeafLabels, totalSubtreeLength, branchScale, and nodeSize.
//
//   - node-gap offset — world-space (NOT divided by the view scale) so it
//     scales with the tree on zoom, holding a constant gap to the node marker
//     (which is itself world-space).  The base offset is (nodeSize / 2 + 4):
//     treats nodeSize as a diameter and adds a 4px clearance gap at the
//     unzoomed (scale 1) baseline.
//
//   - alignLeafLabels — when true, pushes every leaf to the same radial
//     distance as the deepest leaf, adding
//     (totalSubtreeLength - node.distanceFromRoot) * branchScale
//     to the outward offset.
//
//   - The outward direction is always node.angle (radians), projected along
//     (cos(angle), sin(angle)).
//
//   - nodeSize is always included (props.showShapes is true by default).
//     If props.showShapes is false the caller may pass nodeSize = 0; the
//     function handles that gracefully.

/**
 * Build a per-node text-position function. Both the node-gap offset and the
 * align-labels term are world-space distances (NOT divided by the view scale),
 * so labels track the tree as it zooms. The base node-gap offset
 * (nodeSize / 2 + 4) treats nodeSize as a diameter with a 4px clearance gap
 * at the unzoomed (scale 1) baseline.
 *
 * @param {object} graph
 * @param {object} props
 * @returns {function(node): [number, number]}
 */
function makeTextPositionAccessor(graph, props) {
  const alignLeafLabels = props.alignLabels ?? false;
  const totalSubtreeLength = graph.root.totalSubtreeLength;
  const branchScale = graph.branchScale ?? props.branchScale ?? 1;
  // ?? only substitutes null/undefined, not NaN, so guard explicitly: a NaN
  // nodeSize would propagate to [NaN, NaN] coords and render nothing silently.
  const nodeSize = Number.isFinite(props.nodeSize) && props.nodeSize >= 0 ? props.nodeSize : 14;
  const nodeRadius = (nodeSize * nodeLayoutScale(props)) / 2 + 4;

  return function textPosition(node) {
    let offset = nodeRadius;
    if (alignLeafLabels) {
      offset += (totalSubtreeLength - node.distanceFromRoot) * branchScale;
    }
    return [
      node.x + offset * Math.cos(node.angle),
      node.y + offset * Math.sin(node.angle),
    ];
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function renderLeafLabels(graph, props, scale = 1) {
  if (!props.showLabels || !props.showLeafLabels) {
    return null;
  }

  const s = Number.isFinite(scale) && scale > 0 ? scale : 1;
  const leavesPerLabel = computeLeavesPerLabel(graph, props, s);
  const labelledLeafNodes = getLabelledLeafNodes(graph, leavesPerLabel, props.type);
  const textPosition = makeTextPositionAccessor(graph, props);

  const fontFamily = props.fontFamily ?? "monospace";
  // Guard finiteness: a non-finite fontSize would write "Infinity"/"NaN" to the
  // font-size attribute (invalid SVG length, silently ignored -> text reverts to
  // inherited size and the hybrid size cap stops applying).
  const fontSize = Number.isFinite(props.fontSize) && props.fontSize > 0 ? props.fontSize : 16;

  const textElements = [];
  for (const node of labelledLeafNodes) {
    const [x, y] = textPosition(node);
    const degrees =
      (node.angle / Angles.Degrees360) * 360 + (node.inverted ? 180 : 0);
    const labelText = node.label ?? node.id ?? node.name ?? "";
    textElements.push(
      el(
        "text",
        {
          x: x,
          y: y,
          "text-anchor": node.inverted ? "end" : "start",
          "dominant-baseline": "middle",
          transform: `rotate(${degrees},${x},${y})`,
          "data-node-id": node.id,
        },
        [labelText],
      ),
    );
  }

  return el(
    "g",
    {
      class: "leaf-labels",
      "font-family": fontFamily,
      // Hybrid-capped font: fontSize / max(1, scale). Below scale 1 the divisor
      // is 1, so the text is world-space (grows and shrinks with the tree like
      // the node markers and metadata blocks, never overflowing the blocks). At
      // scale >= 1 it divides by scale, locking the on-screen size to a constant
      // capped at the configured fontSize so labels never look oversized.
      "font-size": fontSize / Math.max(1, s),
    },
    textElements,
  );
}
